import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, XCircle } from 'lucide-react';
import { useDocumentHead } from '@/hooks/useDocumentHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
});

const NEUTRAL_MESSAGE = "If an account exists for this email, we've sent a reset link.";

const FieldError = ({ msg }) => msg ? (
  <p className="flex items-center gap-1 text-xs text-red-600 mt-1"><XCircle size={12} /> {msg}</p>
) : null;

const inputCls = (err) =>
  `w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 text-sm transition-colors ${
    err ? 'border-red-400 focus:ring-red-300 bg-red-50' : 'border-border focus:ring-primary'
  }`;

const ForgotPassword = () => {
  useDocumentHead({ robots: 'noindex' });
  const [sent, setSent] = useState(false);
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const onSubmit = async ({ email }) => {
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
    } catch {
      // Show the same neutral message either way.
    }
    setSent(true);
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-screen pt-32 pb-12 px-4 flex items-center justify-center">
      <div className="w-full max-w-md rounded-2xl overflow-hidden border border-border bg-white shadow-sm">
        <div className="bg-primary px-6 py-5">
          <h1 className="font-serif text-2xl font-bold text-white">Forgot password</h1>
          <p className="text-white/80 text-sm">We'll email you a link to reset it</p>
        </div>

        {sent ? (
          <div className="px-6 pb-6 pt-5 space-y-4">
            <p data-testid="forgot-password-message" className="text-sm text-foreground">{NEUTRAL_MESSAGE}</p>
            <Link to="/login" className="text-primary font-semibold hover:underline text-sm">Back to login</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="px-6 pb-6 pt-4 space-y-5" noValidate>
            <div>
              <label htmlFor="forgot-email" className="block text-sm font-medium text-foreground mb-1.5">Email Address</label>
              <input {...register('email')} id="forgot-email" data-testid="forgot-password-email-input"
                type="email" placeholder="your@email.com" className={inputCls(errors.email)} />
              <FieldError msg={errors.email?.message} />
            </div>

            <button type="submit" disabled={isSubmitting} data-testid="forgot-password-submit-button"
              className="w-full bg-primary text-white py-2.5 rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm flex items-center justify-center gap-2">
              {isSubmitting ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : 'Send reset link'}
            </button>

            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="text-primary font-semibold hover:underline">Back to login</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
